import { IDocument, IDocumentQuery } from '@/types/app';
import { Button, Form, Grid, Input, Space, Spin, Tag, Tooltip } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FileTwoTone, DownloadOutlined, PlusOutlined, FilterOutlined } from '@ant-design/icons';
import { downloadFileFromURL, getBreakPoint, matchCountryCodeToFlag, matchCountryCodeToLabel, matchValueToLabel } from '@/utils';
import { dateFormat, pageSizeOptions } from '@/common/constants/format';
import TableWithScrollButtons from '@/components/Shared/TableWithScrollButtons';
import UploadDocumentModal from '@/components/Modals/UploadDocumentModal';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from '@/stores';
import { documentSuitesActions } from '@/stores/documentSuites';
import Paragraph from 'antd/es/typography/Paragraph';
import { UserRole } from '@/common/constants/enums';
import { Link } from 'react-router-dom';
import { DownloadIcon, EyeIcon, SearchIcon } from '@/components/common/IconComponents';
import { SorterResult } from 'antd/es/table/interface';
import FilterDocumentModal from '@/components/Modals/FilterDocumentModal';

const { useBreakpoint } = Grid;

const DocumentSuite = () => {
  const screens = getBreakPoint(useBreakpoint());
  const isMobile = screens.length === 0 ? false : !screens.includes('xl');
  const { userInfo } = useSelector((state) => state.auth);
  const { constants, countries } = useSelector((state) => state?.app);
  const { data, pagination, filter, loading } = useSelector((state) => state?.documentSuites);
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);

  const [seeAllContentList, setSeeAllContentList] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    return () => {
      dispatch(documentSuitesActions.resetFilter());
    };
  }, []);

  useEffect(() => {
    dispatch(documentSuitesActions.getDocuments(filter));
  }, [filter]);

  const [loadingInterval, setLoadingInterval] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<any>('');

  useEffect(function () {
    let interval;
    interval = setInterval(()=>{
      dispatch(documentSuitesActions.getDocuments({ ...filter,...pagination, keyword: searchText, sortBy }));
      setLoadingInterval(true);
    }, 5000);
    return ()=>{
      clearInterval(interval);
    }
  }, [pagination, filter, searchText, sortBy]);

  const onChangeFilter = (value: IDocumentQuery) => {
    dispatch(
      documentSuitesActions.setFilter({
        ...filter,
        ...value,
      }),
    );
  };

  const columns: ColumnsType<IDocument> = [
    {
      title: 'Actions',
      key: 'action',
      fixed: 'left',
      width: 100,
      align: 'center',
      render: (_, record) => (
        record?.attachment?.fileURL?<Space size="middle">
          <Tooltip title="Open Document">
            <Button
              type="text"
              size="small"
              onClick={() => {
                window.open(record?.attachment?.fileURL);
              }}
            >
              <EyeIcon />
            </Button>
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="text"
              size="small"
              onClick={() => {
                downloadFileFromURL(record?.attachment?.fileURL, record?.attachment?.metadata?.name);
              }}
            >
              <DownloadIcon />
            </Button>
          </Tooltip>
        </Space>:null
      ),
    },
    {
      title: 'Document Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      sorter: true,
      render: (value, elm) => {
        // return (
        //   <Paragraph
        //     ellipsis={{
        //       rows: 2,
        //       expandable: false,
        //       tooltip: value,
        //     }}
        //   >
        //     {value}
        //   </Paragraph>
        // );
        return (
          <div
            className={`${seeAllContentList.includes(elm.id || '') ? '' : 'text-truncate'}`}
            onClick={() =>
              seeAllContentList.includes(elm.id || '')
                ? setSeeAllContentList(seeAllContentList.filter((item) => item !== elm.id))
                : setSeeAllContentList([...seeAllContentList, elm.id || ''])
            }
          >
            {value}
          </div>
        );
      },
    },
    {
      title: 'File Name',
      dataIndex: ['attachment', 'metadata', 'name'],
      key: 'fileName',
      width: 300,
      sorter: true,
      render: (value, record) => {
        return (
          <>
            {value ? (
              <Paragraph
                className="mb-0"
                ellipsis={{
                  rows: 1,
                  // expandable: false,
                  // tooltip: value,
                }}
                title={value}
              >
                {value}
              </Paragraph>
            ) : null}
          </>
        );
      },
    },
    // {
    //   title: 'Document Type',
    //   dataIndex: 'type',
    //   key: 'type',
    //   width: 200,
    //   sorter: true,
    //   render: (val) =>
    //     val ? <Tag className="new-hire-tag rounded text-uppercase">{matchValueToLabel(constants?.documentTypes || [], val)}</Tag> : null,
    // },
    // {
    //   title: "Department",
    //   dataIndex: "department",
    //   key: "department",
    //   width: 200,
    //   render: (val) => matchValueToLabel(constants?.departments || [], val),
    // },
    // {
    //   title: 'Employee',
    //   dataIndex: 'employee',
    //   key: 'employee',
    //   width: 200,
    //   sorter: true,
    //   render: (val, record) => (
    //     <Paragraph
    //       className="mb-0"
    //       ellipsis={{
    //         rows: 1,
    //         // expandable: false,
    //         // tooltip: `${record?.employee?.forename} ${record?.employee?.surname}`,
    //       }}
    //       title={`${record?.employee?.name}`||(`${record?.employee?.forename} ${record?.employee?.surname}`)}
    //     >
    //       {`${record?.employee?.name}`|| (record?.employee?.forename+ " " + record?.employee?.surname)}
    //     </Paragraph>
    //   ),
    // },
    {
      title: 'Date Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      sorter: true,
      render: (val) => dayjs.unix(val).format(dateFormat),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 200,
      sorter: true,
      render: (item) =>
        item && (
          <Space>
            <img src={item?.flag} width="16" height="12" alt={`${item?.name}`} className="mr-1" />
            {item?.name}
          </Space>
        ),
    },
  ];

  const handleOpenModal = (e: boolean) => {
    setIsModalOpen(e);
  };

  const handleOpenFilterModal = (e: boolean) => {
    setIsFilterModalOpen(e);
  };

  const handleSearchSubmit = (e: string) => {
    const keyword = e.trim();
    setSearchText(keyword);
    onChangeFilter({ keyword });
    setLoadingInterval(false);
  };

  const handleSortAndPaginationChange = (
    pagination: TablePaginationConfig,
    sorter: SorterResult<IDocument> | SorterResult<IDocument>[],
  ) => {
    dispatch(
      documentSuitesActions.getDocuments({
        ...filter,
        page: pagination.current,
        limit: pagination.pageSize,
        keyword: searchText,
        sortBy: !Array.isArray(sorter) && sorter.order ? `${sorter.field} ${sorter.order === 'ascend' ? 'ASC' : 'DESC'}` : undefined,
      }),
    );
    setSortBy(!Array.isArray(sorter) && sorter.order ? `${sorter.field} ${sorter.order === 'ascend' ? 'ASC' : 'DESC'}` : undefined);
    setLoadingInterval(false);
  };

  const handleSubmitDocument = (values: IDocument) => {
    let newValues: IDocument = { ...values, type: 'guide' };

    dispatch(documentSuitesActions.createDocument(newValues))
      .unwrap()
      .then(() => {
        dispatch(documentSuitesActions.getDocuments(filter));
        setIsModalOpen(false);
      });
  };

  const handleSubmitFilter = (values: IDocumentQuery) => {
    let newValues: IDocumentQuery = { ...values, types: ['guide'] };
    onChangeFilter(newValues);
  };

  return (
    <div className="container-fluid pt-4">
      <Spin tip="Loading" size="large" spinning={loading[documentSuitesActions.getDocuments.typePrefix]&&!loadingInterval}>
        <div className="d-flex justify-content-between">
          <p className="page-listing-title">Documents & Guides</p>
        </div>

        <div className="bg-white rounded border">
          <div className="card-header d-flex justify-content-between">
            <Input
              prefix={<SearchIcon className="mr-0" />}
              placeholder="Search"
              onPressEnter={(e: any) => handleSearchSubmit(e?.target?.value)}
              className="w-100 mr-2 page-listing-search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {userInfo?.role === UserRole.company ? (
              <Space>
                <Button className="d-flex align-items-center" type="primary" onClick={() => handleOpenModal(true)}>
                  <PlusOutlined />
                  Import
                </Button>
                <Button className="d-flex align-items-center" type="primary" ghost onClick={() => handleOpenFilterModal(true)}>
                  <FilterOutlined />
                  Filter
                </Button>
              </Space>
            ) : null}
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <TableWithScrollButtons
                columns={columns}
                dataSource={data}
                size="small"
                scroll={{ x: 800 }}
                onChange={(pagination, filter, sorter) => {
                  handleSortAndPaginationChange(pagination, sorter);
                }}
                pagination={{
                  showSizeChanger: true,
                  defaultCurrent: 1,
                  total: pagination.total,
                  position: ['bottomLeft'],
                  pageSizeOptions: pageSizeOptions,
                  showTotal: (total: number, range: number[]) => 'Showing ' + range[0] + ' to ' + range[1] + ' of ' + total + ' entries',
                }}
                // onChange={(pagination) => {
                //   onChangeFilter({
                //     page: pagination?.current,
                //     limit: pagination?.pageSize,
                //   });
                // }}
              />
            </div>
          </div>
        </div>
        {isModalOpen && (
          <UploadDocumentModal
            open={isModalOpen}
            destroyOnClose={true}
            onCancel={() => setIsModalOpen(false)}
            onSubmit={handleSubmitDocument}
            confirmLoading={loading[documentSuitesActions?.createDocument.typePrefix]}
            countries={countries}
            hideDocumentType={true}
            isChangeDescriptionToTitle
          />
        )}
        {/* {isFilterModalOpen && ( */}
        <FilterDocumentModal
          open={isFilterModalOpen}
          // destroyOnClose={true}
          onCancel={() => setIsFilterModalOpen(false)}
          onSubmit={handleSubmitFilter}
        />
        {/* )} */}
      </Spin>
    </div>
  );
};

export default DocumentSuite;
